import axiosInstance from "../../../middlewares/axiosInstance";
import {
  APIResponse,
  ListGRNsRequest,
  ListGRNsResponse,
  CreateGRNRequest,
  CreateGRNResponse,
  UpdateGRNRequest,
  GetGRNRequest,
  ApproveGRNRequest,
  ApproveGRNResponse,
  RejectGRNRequest,
  SendForApprovalRequest,
  MarkInvoicedRequest,
  DeleteGRNRequest,
  GetGRNActivityLogsRequest,
  GetGRNActivityLogsResponse,
  GoodsReceiptNote,
  GRNAutoFillData,
  CheckIngredientExistsRequest,
  CheckIngredientExistsResponse,
} from "../types/grn.types";

class GRNService {
  private baseUrl = "/business-admin/grn";

  // List all GRNs
  async listGRNs(
    params: ListGRNsRequest
  ): Promise<APIResponse<ListGRNsResponse>> {
    const response = await axiosInstance.post(`${this.baseUrl}/list`, params);

    // Handle the nested data structure from backend
    if (response.data?.success && response.data?.data?.data) {
      const transformedResponse = {
        success: response.data.success,
        data: {
          grns: response.data.data.data,
          totalCount: response.data.data.metadata?.total?.value || 0,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          totalPages: Math.ceil(
            (response.data.data.metadata?.total?.value || 0) / params.pageSize
          ),
        },
      };
      return transformedResponse;
    }

    return response.data;
  }

  // Get auto-fill data for GRN from PO
  async getGRNAutoFillData(
    po_id: string
  ): Promise<APIResponse<GRNAutoFillData>> {
    const response = await axiosInstance.post(`${this.baseUrl}/autofill`, {
      po_id,
    });
    return response.data;
  }

  // Create new GRN
  async createGRN(
    data: CreateGRNRequest
  ): Promise<APIResponse<CreateGRNResponse>> {
    console.log("🚀 Creating GRN with data:", JSON.stringify(data, null, 2));
    console.log("📍 API endpoint:", `${this.baseUrl}/create`);

    try {
      const response = await axiosInstance.post(`${this.baseUrl}/create`, data);
      console.log("✅ GRN Create API response:", response);
      return response.data;
    } catch (error: any) {
      console.error("💥 GRN Create API Error:", error);
      console.error("💥 Error response:", error.response?.data);
      console.error("💥 Error status:", error.response?.status);
      console.error("💥 Error headers:", error.response?.headers);
      throw error;
    }
  }

  // Update existing GRN
  async updateGRN(
    data: UpdateGRNRequest
  ): Promise<APIResponse<GoodsReceiptNote>> {
    const response = await axiosInstance.post(`${this.baseUrl}/update`, data);
    return response.data;
  }

  // Send GRN for approval (dedicated workflow endpoint)
  async sendForApproval(
    params: SendForApprovalRequest
  ): Promise<APIResponse<GoodsReceiptNote>> {
    const response = await axiosInstance.post(`${this.baseUrl}/submit`, params);
    return response.data;
  }

  // Get single GRN by ID
  async getGRN(params: GetGRNRequest): Promise<APIResponse<GoodsReceiptNote>> {
    const response = await axiosInstance.post(`${this.baseUrl}/get`, params);
    return response.data;
  }

  // Approve GRN (creates inventory entries)
  async approveGRN(
    params: ApproveGRNRequest
  ): Promise<APIResponse<ApproveGRNResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/approve`,
      params
    );
    return response.data;
  }

  // Reject GRN
  async rejectGRN(
    params: RejectGRNRequest
  ): Promise<APIResponse<GoodsReceiptNote>> {
    const response = await axiosInstance.post(`${this.baseUrl}/reject`, params);
    return response.data;
  }

  // Mark GRN as invoiced
  async markAsInvoiced(
    params: MarkInvoicedRequest
  ): Promise<APIResponse<GoodsReceiptNote>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/mark-invoiced`,
      params
    );
    return response.data;
  }

  // Delete GRN
  async deleteGRN(params: DeleteGRNRequest): Promise<APIResponse<void>> {
    const response = await axiosInstance.post(`${this.baseUrl}/delete`, params);
    return response.data;
  }

  // Get GRN activity logs
  async getGRNActivityLogs(
    params: GetGRNActivityLogsRequest
  ): Promise<APIResponse<GetGRNActivityLogsResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/activity-logs`,
      params
    );
    return response.data;
  }

  // Get received POs (for GRN creation)
  async getReceivedPOs(params: {
    pageIndex: number;
    pageSize: number;
    searchQuery?: string;
  }): Promise<
    APIResponse<{
      purchase_orders: Array<{
        _id: string;
        po_number: string;
        vendor_details: {
          supplier_name: string;
          supplier_id: string;
        };
        total_amount: number;
        currency: string;
        received_at: number;
        items: Array<{
          item_id: string;
          item_name: string;
          quantity: number;
          unit: string;
          unit_price: number;
        }>;
      }>;
      totalCount: number;
      pageIndex: number;
      pageSize: number;
    }>
  > {
    const response = await axiosInstance.post(
      `/business-admin/po/received-pos`,
      params
    );
    return response.data;
  }

  // Check if PO already has GRN
  async checkPOGRN(po_id: string): Promise<
    APIResponse<{
      hasGRN: boolean;
      grn?: GoodsReceiptNote;
    }>
  > {
    const response = await axiosInstance.post(`${this.baseUrl}/check-po-grn`, {
      po_id,
    });
    return response.data;
  }

  // Get warehouses for dropdown
  async getWarehouses(): Promise<
    APIResponse<{
      data: Array<{
        id: string;
        name: string;
        code: string;
        location?: string;
        is_active: boolean;
      }>;
      metadata?: any;
    }>
  > {
    try {
      console.log(
        "🚀 GRN Service: Making POST request to /business-admin/warehouses/list"
      );
      const response = await axiosInstance.post(
        "/business-admin/warehouses/list",
        {}
      );
      console.log("🎯 Raw warehouses API response:", response); // Debug log
      console.log("📦 Response data:", response.data);
      console.log("📊 Response status:", response.status);
      return response.data;
    } catch (error) {
      console.error("💥 Warehouses API error:", error);
      if (error instanceof Error) {
        console.error("💥 Error message:", error.message);
      }
      throw error;
    }
  }

  // Generate batch number
  generateBatchNumber(): string {
    const timestamp = new Date().getTime().toString(36);
    const random = Math.random().toString(36).substring(2, 5);
    return `BATCH-${timestamp}-${random}`.toUpperCase();
  }

  // Validate GRN data before submission
  validateGRNData(data: CreateGRNRequest | UpdateGRNRequest): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Check if PO ID is provided (for create)
    if ("po_id" in data && !data.po_id) {
      errors.push("Purchase Order selection is required");
    }

    // Check warehouse
    if (!data.warehouse_id) {
      errors.push("Warehouse selection is required");
    }

    // Check items
    if (!data.items || data.items.length === 0) {
      errors.push("At least one item must be received");
    }

    // Validate each item
    data.items?.forEach((item, index) => {
      if (!item.item_id) {
        errors.push(`Item ${index + 1}: Item selection is required`);
      }
      if (!item.received_quantity || item.received_quantity <= 0) {
        errors.push(
          `Item ${index + 1}: Received quantity must be greater than 0`
        );
      }
      if (!item.condition) {
        errors.push(`Item ${index + 1}: Condition selection is required`);
      }
      // If condition is damaged or partially damaged, remarks should be required
      if (
        (item.condition === "damaged" ||
          item.condition === "partially_damaged") &&
        !item.remarks?.trim()
      ) {
        errors.push(
          `Item ${index + 1}: Remarks are required for damaged items`
        );
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Check if ingredient exists (Phase 2)
  async checkIngredientExists(
    params: CheckIngredientExistsRequest
  ): Promise<APIResponse<CheckIngredientExistsResponse["data"]>> {
    const response = await axiosInstance.post(
      `/business-admin/ingredients/check-exists`,
      params
    );
    return response.data;
  }
}

export const grnService = new GRNService();
